import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';

import { LogEntry } from '@core/classes';
import { ExtendedLogDetails, LogLevel, LogPublisher } from '@shared/models/log.interface';

import { AppService } from '@shared/services/core/app.service';
import { LogPublisherService } from '@shared/services/core/log-publisher.service';

@Injectable({
  providedIn: 'root'
})
export class LogService {
  level: LogLevel = LogLevel.ALL;
  publishers: Array<LogPublisher> = [];

  constructor(
    private router: Router,
    private appService: AppService,
    private deviceInfoService: DeviceDetectorService,
    private logPublisherService: LogPublisherService
  ) {
    this.level = this.appService.getAppSettings().log.defaultLevel;
    this.publishers = this.logPublisherService.publishers;
  }

  debug(message: string, extendedLogs?: ExtendedLogDetails, usingMobileDevice: boolean = false, token?: string, jti?: string) {
    this.createLog(message, LogLevel.DEBUG, extendedLogs, usingMobileDevice, token, jti);
  }

  info(msg: string, extendedLogs?: ExtendedLogDetails, usingMobileDevice: boolean = false, token?: string, jti?: string) {
    this.createLog(msg, LogLevel.INFO, extendedLogs, usingMobileDevice, token, jti);
  }

  warn(msg: string, extendedLogs?: ExtendedLogDetails, usingMobileDevice: boolean = false, token?: string, jti?: string) {
    this.createLog(msg, LogLevel.WARN, extendedLogs, usingMobileDevice, token, jti);
  }

  error(msg: string, extendedLogs?: ExtendedLogDetails, usingMobileDevice: boolean = false, token?: string, jti?: string) {
    this.createLog(msg, LogLevel.ERROR, extendedLogs, usingMobileDevice, token, jti);
  }

  fatal(msg: string, extendedLogs?: ExtendedLogDetails, usingMobileDevice: boolean = false, token?: string, jti?: string) {
    this.createLog(msg, LogLevel.FATAL, extendedLogs, usingMobileDevice, token, jti);
  }

  appLogin(msg: string, extendedLogs?: ExtendedLogDetails, usingMobileDevice: boolean = false, token?: string, jti?: string) {
    this.createLog(msg, LogLevel.APPLOGIN, extendedLogs, usingMobileDevice, token, jti);
  }

  appLogout(msg: string, extendedLogs?: ExtendedLogDetails, usingMobileDevice: boolean = false, token?: string, jti?: string) {
    this.createLog(msg, LogLevel.APPLOGOUT, extendedLogs, usingMobileDevice, token, jti);
  }

  all(msg: string, extendedLogs?: ExtendedLogDetails, usingMobileDevice: boolean = false, token?: string, jti?: string) {
    this.createLog(msg, LogLevel.ALL, extendedLogs, usingMobileDevice, token, jti);
  }

  private createLog(message: string, level: LogLevel, extended?: ExtendedLogDetails, usingMobileDevice: boolean = false, token?: string, jti?: string) {
    if (this.shouldIncludeLogsOf(level)) {
      let logEntry = new LogEntry();
      logEntry.basicLogDetails.screen_route = this.router.url.split("?")[0];
      logEntry.basicLogDetails.log_message = message;
      logEntry.basicLogDetails.level = level;
      logEntry.extendedLogDetails = extended;
      const info = this.deviceInfoService.getDeviceInfo();
      logEntry.deviceLogDetails = { ...info, device_type: info.deviceType };

      for (let publisher of this.publishers) {
        publisher.log(logEntry, usingMobileDevice, token, jti).subscribe();
      }
    }
  }

  private shouldIncludeLogsOf(level: LogLevel) {
    let shouldInclude: boolean = false;

    if ((level >= this.level && level !== LogLevel.OFF) || (this.level === LogLevel.ALL)) {
      shouldInclude = true;
    }
    return shouldInclude;
  }
}
